import type { FC, ReactNode } from 'react'

import Link from 'next/link'
import { useCallback, useRef, useState } from 'react'
import { motion, useMotionValueEvent, useScroll } from 'motion/react'
import { DayClock } from '@/components/generals/day-clock'
import { DomeMark } from '@/components/generals/dome-mark'
import { SiteMenu } from '@/components/generals/site-menu'
import { cn } from '@/lib/utils'
import { contact } from '@/constants/site'

interface SiteHeaderProps {
    clock?: boolean
}

export const SiteHeader: FC<SiteHeaderProps> = ({ clock = false }): ReactNode => {
    const [open, setOpen] = useState(false)
    const [hidden, setHidden] = useState(false)
    const [scrolled, setScrolled] = useState(false)
    const hiddenRef = useRef(false)
    const { scrollY } = useScroll()

    // Steps out of the way while reading down, comes back as soon as you scroll up.
    useMotionValueEvent(scrollY, 'change', current => {
        const previous = scrollY.getPrevious() ?? 0
        const delta = current - previous

        if ((current > 40) !== scrolled) setScrolled(current > 40)

        if (Math.abs(delta) < 4) return

        const next = delta > 0 && current > 180

        if (next !== hiddenRef.current) {
            hiddenRef.current = next
            setHidden(next)
        }
    })

    const close = useCallback(() => setOpen(false), [])

    return (
        <>
            <motion.header
                className='fixed inset-x-0 top-0 z-50 text-tone-fg'
                initial={false}
                animate={{ y: hidden && !open ? '-105%' : '0%' }}
                transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
            >
                <div
                    aria-hidden='true'
                    className={cn(
                        'pointer-events-none absolute inset-0 -bottom-6 bg-linear-to-b from-tone via-tone/75 to-transparent transition-opacity duration-700',
                        scrolled ? 'opacity-100' : 'opacity-0'
                    )}
                />

                <div className='relative flex h-16 items-center justify-between gap-6 px-5 md:h-20 md:px-10'>
                    <Link href='/' className='flex items-center gap-3' aria-label='Unity Space Berlin, home'>
                        <DomeMark className='w-5 md:w-6' />
                        <span className='flex items-baseline gap-2'>
                            <span className='font-heading text-xl font-medium leading-none tracking-tight md:text-2xl'>
                                Unity Space
                            </span>
                            <span className='font-mono text-[10px] uppercase tracking-[0.22em] text-tone-muted'>
                                Berlin
                            </span>
                        </span>
                    </Link>

                    {clock && <DayClock className='absolute left-1/2 hidden -translate-x-1/2 lg:flex' />}

                    <div className='flex items-center gap-5 md:gap-8'>
                        <a
                            href={contact.bookingUrl}
                            target='_blank'
                            rel='noopener noreferrer'
                            className='link-underline hidden font-mono text-[11px] uppercase tracking-[0.18em] md:inline'
                        >
                            Book the venue
                        </a>

                        <button
                            type='button'
                            onClick={() => setOpen(true)}
                            aria-expanded={open}
                            aria-controls='site-menu'
                            className='group flex items-center gap-3 font-mono text-[11px] uppercase tracking-[0.18em]'
                        >
                            Menu
                            <span className='relative grid size-10 place-items-center border border-tone-line transition-colors duration-500 group-hover:bg-tone-fg group-hover:text-tone'>
                                <span className='flex w-4 flex-col gap-[5px]' aria-hidden='true'>
                                    <span className='h-px w-full bg-current transition-transform duration-500 group-hover:translate-x-0.5' />
                                    <span className='h-px w-2/3 bg-current transition-all duration-500 group-hover:w-full' />
                                </span>
                            </span>
                        </button>
                    </div>
                </div>
            </motion.header>

            <SiteMenu open={open} onClose={close} />
        </>
    )
}
